import {
  EventDispatcher,
  ValueDispatcher,
  RendererResult,
  RendererState,
  TimeEstimator,
} from '@cascade/core';
import type {FC} from 'react';

/**
 * The subset of Vite's HMR client API that we need.
 */
interface HotClient {
  send(event: string, data?: any): void;
  on(event: string, cb: (...args: any[]) => void): void;
}

/**
 * Settings for in-browser export via @remotion/web-renderer.
 */
export interface InBrowserRenderSettings {
  component: FC;
  durationInFrames: number;
  fps: number;
  width: number;
  height: number;
  filename: string;
}

/**
 * Replaces MC's Renderer class. Supports two rendering paths:
 *
 * 1. **Server-side** (dev mode): Renders via `@remotion/renderer` through
 *    the WebSocket `cascade:remotion-render` endpoint in vite-plugin.
 *    Requires HMR client injection.
 *
 * 2. **In-browser** (SaaS mode): Renders via `@remotion/web-renderer`'s
 *    `renderMediaOnWeb()` entirely in the browser, producing an MP4 blob
 *    that is downloaded directly. No server required.
 */
export class RemotionRendererStub {
  public get onStateChanged() {
    return this._state.subscribable;
  }
  private readonly _state = new ValueDispatcher(RendererState.Initial);

  public get onFrameChanged() {
    return this._frame.subscribable;
  }
  private readonly _frame = new ValueDispatcher(0);

  public get onFinished() {
    return this._finished.subscribable;
  }
  private readonly _finished = new EventDispatcher<RendererResult>();

  /** Total frames in the current render. */
  public get onTotalFramesChanged() {
    return this._totalFrames.subscribable;
  }
  private readonly _totalFrames = new ValueDispatcher(0);

  /** Current render stage: rendering, encoding, audio-mixing. */
  public get onStageChanged() {
    return this._stage.subscribable;
  }
  private readonly _stage = new ValueDispatcher<string>('rendering');

  /** Render start timestamp for fps calculation. */
  private _renderStartTime = 0;

  public readonly estimator = new TimeEstimator();

  /** No MC Stage needed -- Remotion renders server-side. */
  public readonly stage: null = null;

  private hot: HotClient | null;

  /** AbortController for in-browser renders. */
  private _abortController: AbortController | null = null;

  constructor(hot?: HotClient | null) {
    this.hot = hot ?? null;
  }

  /** Whether server-side rendering is available (HMR connected). */
  public get canRenderServer(): boolean {
    return this.hot !== null;
  }

  /**
   * Current render fps (frames rendered per second of wall time).
   */
  public get renderFps(): number {
    const elapsed = (Date.now() - this._renderStartTime) / 1000;
    if (elapsed <= 0) return 0;
    return this._frame.current / elapsed;
  }

  /**
   * Trigger a Remotion render via the vite-plugin WebSocket endpoint.
   */
  public render(settings: {name: string; audioClips?: unknown[]; audioTracks?: unknown[]; [key: string]: unknown}) {
    if (this._state.current !== RendererState.Initial) return;
    if (!this.hot) {
      console.warn('Cannot render: no HMR connection');
      return;
    }

    this._state.current = RendererState.Working;
    this._frame.current = 0;
    this._totalFrames.current = 0;
    this._stage.current = 'starting';
    this._renderStartTime = Date.now();
    this.estimator.reset();

    console.log('[RemotionRendererStub] render() called, state → Working');

    const hot = this.hot;

    const handleProgress = (data: {
      progress: number;
      renderedFrames?: number;
      encodedFrames?: number;
      totalFrames?: number;
      stage?: string;
    }) => {
      console.log('[RemotionRendererStub] progress:', data);
      this._frame.current = data.renderedFrames ?? 0;
      if (data.totalFrames) this._totalFrames.current = data.totalFrames;
      if (data.stage) this._stage.current = data.stage;
      this.estimator.update(data.progress);
    };

    const handleComplete = (data: {outputPath?: string; duration?: number; frames?: number; size?: number}) => {
      console.log('[RemotionRendererStub] complete:', data);
      this._state.current = RendererState.Initial;
      this._finished.dispatch(RendererResult.Success);
    };

    const handleError = (data: {error: string}) => {
      console.error('Render error:', data.error);
      this._state.current = RendererState.Initial;
      this._finished.dispatch(RendererResult.Error);
    };

    const handleAborted = () => {
      console.log('Render aborted by user.');
      this._state.current = RendererState.Initial;
      this._finished.dispatch(RendererResult.Aborted);
    };

    hot.on('cascade:remotion-render-progress', handleProgress);
    hot.on('cascade:remotion-render-complete', handleComplete);
    hot.on('cascade:remotion-render-error', handleError);
    hot.on('cascade:remotion-render-aborted', handleAborted);

    hot.send('cascade:remotion-render', {
      compositionId: settings.name,
      outFile: settings.outFile,
      fps: settings.fps,
      codec: settings.codec,
      crf: settings.crf,
      audioClips: settings.audioClips,
      audioTracks: settings.audioTracks,
    });
  }

  /**
   * Render entirely in the browser via @remotion/web-renderer.
   * Produces an MP4 blob and triggers a browser download.
   *
   * Drives the same _state/_frame/_totalFrames/_stage/estimator dispatchers
   * as the server-side render() path, so existing progress UI works unchanged.
   */
  public async renderInBrowser(settings: InBrowserRenderSettings): Promise<void> {
    if (this._state.current !== RendererState.Initial) return;

    // Transition to working state
    this._state.current = RendererState.Working;
    this._frame.current = 0;
    this._totalFrames.current = settings.durationInFrames;
    this._stage.current = 'rendering';
    this._renderStartTime = Date.now();
    this.estimator.reset();

    // Create abort controller for cancellation
    this._abortController = new AbortController();

    console.log('[RemotionRendererStub] renderInBrowser() called, state → Working');

    try {
      // Dynamic import to avoid bundling web-renderer in dev mode
      const {renderMediaOnWeb} = await import('@remotion/web-renderer');

      const result = await renderMediaOnWeb({
        composition: {
          id: 'cascade-export',
          component: settings.component as any,
          durationInFrames: settings.durationInFrames,
          fps: settings.fps,
          width: settings.width,
          height: settings.height,
        },
        videoCodec: 'h264',
        onProgress: ({renderedFrames, encodedFrames}) => {
          this._frame.current = renderedFrames;
          const progress = encodedFrames / settings.durationInFrames;
          this.estimator.update(progress);

          // Switch stage label when encoding starts
          if (encodedFrames > 0 && renderedFrames >= settings.durationInFrames) {
            this._stage.current = 'encoding';
          }
        },
        signal: this._abortController.signal,
      });

      // Check if aborted during getBlob
      if (this._abortController.signal.aborted) {
        this._state.current = RendererState.Initial;
        this._finished.dispatch(RendererResult.Aborted);
        return;
      }

      this._stage.current = 'finalizing';

      const blob = await result.getBlob();

      console.log(
        `[RemotionRendererStub] Export complete: ${(blob.size / 1024 / 1024).toFixed(1)} MB`,
      );

      // Trigger browser download
      const {triggerDownload} = await import('../saas/export');
      triggerDownload(blob, `${settings.filename}.mp4`);

      this._state.current = RendererState.Initial;
      this._finished.dispatch(RendererResult.Success);
    } catch (err: any) {
      if (err?.name === 'AbortError' || this._abortController?.signal.aborted) {
        console.log('In-browser render aborted by user.');
        this._state.current = RendererState.Initial;
        this._finished.dispatch(RendererResult.Aborted);
      } else {
        console.error('In-browser render error:', err);
        this._state.current = RendererState.Initial;
        this._finished.dispatch(RendererResult.Error);
      }
    } finally {
      this._abortController = null;
    }
  }

  /**
   * Abort an ongoing render (works for both server-side and in-browser).
   */
  public abort() {
    if (this._state.current !== RendererState.Working) return;
    this._state.current = RendererState.Aborting;

    // Abort in-browser render
    if (this._abortController) {
      this._abortController.abort();
    }

    // Abort server-side render
    this.hot?.send('cascade:remotion-render-abort', {});
  }

  /**
   * Export a single frame (snapshot) via Remotion's renderStill.
   * Only available in dev mode (requires HMR).
   */
  public renderFrame(
    settings: {name: string; fps?: number; [key: string]: unknown},
    time: number,
  ) {
    if (!this.hot) {
      console.warn('Cannot snapshot: no HMR connection');
      return;
    }
    const fps = (settings.fps as number) || 60;
    const frame = Math.round(time * fps);
    this.hot.send('cascade:remotion-snapshot', {
      compositionId: settings.name,
      frame,
    });
  }
}
